import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, closestCorners, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, X, ChevronLeft, ChevronRight, LayoutGrid, List } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { fmtDate } from "../format";

const COLUMNS = [
  { key: "open", label: "To do" },
  { key: "doing", label: "In progress" },
  { key: "blocked", label: "Blocked" },
  { key: "done", label: "Done" },
];
const DONE_KANBAN_LIMIT = 20;
const DONE_LIST_LIMIT = 15;

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function Sheet({ onClose, title, children, testId }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function DueLabel({ a }) {
  if (!a.due_date) return null;
  return a.late
    ? <span className="cmd-due late" data-testid={`due-${a.id}`}>Late · {fmtDate(a.due_date)}</span>
    : <span className="cmd-due">{fmtDate(a.due_date)}</span>;
}

function CardBody({ a, users }) {
  const assignee = users.find((u) => u.id === a.assignee_user_id);
  const creator = a.created_by === "system" ? "Suggested starting point" : a.created_by_name;
  return (
    <>
      <div className="cmd-card-title">{a.title}</div>
      <div className="cmd-card-meta">
        <span className="cmd-card-init">{a.initiative_name}</span>
        {a.owner_role && <span className="cmd-chip">{a.owner_role}</span>}
      </div>
      <div className="cmd-card-foot">
        <DueLabel a={a} />
        {a.priority === "high" && <span className="cmd-tag-high">High</span>}
        {assignee
          ? <span className="cmd-avatar cmd-avatar-sm" title={assignee.name}>{initials(assignee.name)}</span>
          : creator && <span className="cmd-card-creator">{creator}</span>}
      </div>
    </>
  );
}

function SortableCard({ a, users, canEdit, onOpen, onMoveStep, colIdx }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: a.id, disabled: !canEdit,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`cmd-card health-${a.initiative_health || "none"} ${canEdit ? "" : "readonly"}`}
      data-testid={`card-${a.id}`}
    >
      <div onClick={() => onOpen(a)} style={{ cursor: "pointer" }}>
        <CardBody a={a} users={users} />
      </div>
      {canEdit && (
        <div className="cmd-card-arrows">
          <button className="cmd-arrow" disabled={colIdx === 0}
            onClick={() => onMoveStep(a, -1)} aria-label="Move to previous column"
            data-testid={`card-prev-${a.id}`}><ChevronLeft size={14} /></button>
          <button className="cmd-arrow" disabled={colIdx === COLUMNS.length - 1}
            onClick={() => onMoveStep(a, 1)} aria-label="Move to next column"
            data-testid={`card-next-${a.id}`}><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}

function Column({ col, cards, users, canEdit, onOpen, onMoveStep, onShowAllDone }) {
  const shown = col.key === "done" ? cards.slice(0, DONE_KANBAN_LIMIT) : cards;
  return (
    <div className="cmd-col" data-testid={`col-${col.key}`}>
      <div className="cmd-col-head">
        <span>{col.label}</span>
        <span className="cmd-col-count">{cards.length}</span>
      </div>
      <SortableContext items={shown.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div className="cmd-col-body">
          {shown.map((a) => (
            <SortableCard key={a.id} a={a} users={users} canEdit={canEdit}
              onOpen={onOpen} onMoveStep={onMoveStep} colIdx={COLUMNS.findIndex((c) => c.key === col.key)} />
          ))}
          {shown.length === 0 && <div className="cmd-col-empty">Nothing here</div>}
        </div>
      </SortableContext>
      {col.key === "done" && cards.length > DONE_KANBAN_LIMIT && (
        <button className="cmd-link" onClick={onShowAllDone} data-testid="show-all-done">
          Show all done ({cards.length})
        </button>
      )}
    </div>
  );
}

function ActionForm({ initial, initiatives, roles, users, onSaved, onClose }) {
  const editing = !!initial?.id;
  const [f, setF] = useState({
    title: initial?.title || "",
    initiative_id: initial?.initiative_id || "",
    owner_role: initial?.owner_role || "Founder",
    assignee_user_id: initial?.assignee_user_id || "",
    due_date: initial?.due_date || "",
    status: initial?.status || "open",
    priority: initial?.priority || "normal",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const ownerOptions = roles.filter((r) => r !== "Investor");

  const chosenInit = initiatives.find((i) => i.id === f.initiative_id);
  const assigneeOptions = users.filter((u) =>
    !u.verticals?.length || !chosenInit || u.verticals.includes(chosenInit.vertical));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.initiative_id) { toast.error("Pick an initiative."); return; }
    setBusy(true);
    const payload = {
      ...f,
      assignee_user_id: f.assignee_user_id || null,
      due_date: f.due_date || null,
      notes: f.notes || null,
    };
    try {
      const { data } = editing
        ? await cmdApi.patch(`/actions/${initial.id}`, payload)
        : await cmdApi.post("/actions", payload);
      toast.success(editing ? "Action updated" : "Action added");
      onSaved(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not save action.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="action-form">
      <div className="cmd-field">
        <label>What needs to happen</label>
        <input className="cmd-input" value={f.title} onChange={(e) => set("title", e.target.value)}
          required maxLength={300} data-testid="action-form-title" />
      </div>
      <div className="cmd-form-grid">
        <div className="cmd-field">
          <label>Initiative</label>
          <select className="cmd-input" value={f.initiative_id} onChange={(e) => set("initiative_id", e.target.value)}
            required data-testid="action-form-initiative">
            <option value="">Pick an initiative…</option>
            {initiatives.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Owner role</label>
          <select className="cmd-input" value={f.owner_role} onChange={(e) => set("owner_role", e.target.value)}
            data-testid="action-form-owner">
            {ownerOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Assignee (optional)</label>
          <select className="cmd-input" value={f.assignee_user_id} onChange={(e) => set("assignee_user_id", e.target.value)}
            data-testid="action-form-assignee">
            <option value="">Nobody yet</option>
            {assigneeOptions.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Due date</label>
          <input type="date" className="cmd-input" value={f.due_date}
            onChange={(e) => set("due_date", e.target.value)} data-testid="action-form-due" />
        </div>
        <div className="cmd-field">
          <label>Status</label>
          <select className="cmd-input" value={f.status} onChange={(e) => set("status", e.target.value)}
            data-testid="action-form-status">
            {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Priority</label>
          <select className="cmd-input" value={f.priority} onChange={(e) => set("priority", e.target.value)}
            data-testid="action-form-priority">
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>
      <div className="cmd-field">
        <label>Notes</label>
        <textarea className="cmd-input" rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)}
          data-testid="action-form-notes" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="cmd-btn cmd-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="action-form-save">
          {busy ? "Saving…" : editing ? "Save changes" : "Add action"}
        </button>
      </div>
    </form>
  );
}

function ReadOnlyCard({ a, users, onClose }) {
  const assignee = users.find((u) => u.id === a.assignee_user_id);
  return (
    <Sheet onClose={onClose} title={a.title} testId="action-readonly">
      <div className="cmd-detail-grid">
        <div><div className="cmd-detail-label">Initiative</div><div>{a.initiative_name}</div></div>
        <div><div className="cmd-detail-label">Status</div><div>{COLUMNS.find((c) => c.key === a.status)?.label}</div></div>
        <div><div className="cmd-detail-label">Owner role</div><div>{a.owner_role || "—"}</div></div>
        <div><div className="cmd-detail-label">Assignee</div><div>{assignee?.name || "Nobody yet"}</div></div>
        <div><div className="cmd-detail-label">Due</div>
          <div>{a.due_date ? (a.late ? <span className="cmd-danger-text">Late · {fmtDate(a.due_date)}</span> : fmtDate(a.due_date)) : "No due date"}</div>
        </div>
        <div><div className="cmd-detail-label">Priority</div><div>{a.priority === "high" ? "High" : "Normal"}</div></div>
      </div>
      {a.notes && <div className="cmd-detail-section"><h4>Notes</h4><p style={{ margin: 0 }}>{a.notes}</p></div>}
      <p className="cmd-muted" style={{ fontSize: 13, marginTop: 16 }}>
        {a.created_by === "system" ? "Suggested starting point" : `Added by ${a.created_by_name || "someone"}`}
      </p>
    </Sheet>
  );
}

function todayIST() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date()); // YYYY-MM-DD
}
function weekEndIST() {
  const d = new Date(Date.now() + 6 * 86400000);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(d);
}

export default function CmdBoard() {
  const { can } = usePermissions();
  const canEdit = can("board", "edit");
  const [actions, setActions] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("kanban"); // kanban | list
  const [listDoneOnly, setListDoneOnly] = useState(false);
  const [fVertical, setFVertical] = useState("");
  const [fInitiative, setFInitiative] = useState("");
  const [fOwner, setFOwner] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readonlyCard, setReadonlyCard] = useState(null);
  const snapshot = useRef(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (fInitiative) params.set("initiative_id", fInitiative);
      if (fVertical) params.set("vertical", fVertical);
      if (fOwner) params.set("owner_role", fOwner);
      const { data: acts } = await cmdApi.get(`/actions?${params}`);
      setActions(acts.items);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not load the board.");
      setActions([]);
    }
    // Secondary data (initiative names, roles, users) — tolerate 403s for roles
    // like Staff that can edit the board but cannot view the portfolio module.
    try { const { data: inits } = await cmdApi.get("/initiatives"); setInitiatives(inits.items); } catch { /* no portfolio access */ }
    try { const { data: cfg } = await cmdApi.get("/roles-config"); setRoles(cfg.roles || []); } catch { /* non-fatal */ }
    try { const { data: us } = await cmdApi.get("/users"); setUsers(us.items || []); } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); }, [fVertical, fInitiative, fOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const byStatus = useMemo(() => {
    const map = { open: [], doing: [], blocked: [], done: [] };
    for (const a of actions || []) (map[a.status] ||= []).push(a);
    for (const k of Object.keys(map)) {
      map[k].sort(k === "done"
        ? (x, y) => String(y.done_at || "").localeCompare(String(x.done_at || ""))
        : (x, y) => x.sort_index - y.sort_index);
    }
    return map;
  }, [actions]);

  const persistMove = async (action, newStatus, newIndex) => {
    try {
      await cmdApi.patch(`/actions/${action.id}/move`, { status: newStatus, sort_index: newIndex });
    } catch (ex) {
      // rollback
      if (snapshot.current) setActions(snapshot.current);
      toast.error(ex?.response?.data?.detail || "Move rejected — card restored.");
    }
  };

  const applyMove = (id, newStatus, newIndex) => {
    snapshot.current = actions;
    setActions((prev) => prev.map((a) => a.id === id
      ? { ...a, status: newStatus, sort_index: newIndex, done_at: newStatus === "done" ? new Date().toISOString() : null, late: false }
      : a));
    const a = actions.find((x) => x.id === id);
    persistMove(a, newStatus, newIndex);
  };

  const moveStep = (a, dir) => {
    const idx = COLUMNS.findIndex((c) => c.key === a.status);
    const next = COLUMNS[idx + dir];
    if (!next) return;
    const target = (byStatus[next.key] || []).filter((x) => x.id !== a.id);
    const newIndex = target.length ? target[target.length - 1].sort_index + 1024 : 1024;
    applyMove(a.id, next.key, newIndex);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragStart = () => { snapshot.current = actions; };

  const findCol = (id) => COLUMNS.find((c) => (byStatus[c.key] || []).some((a) => a.id === id))?.key
    || (COLUMNS.some((c) => c.key === id) ? id : null);

  const onDragEnd = ({ active, over }) => {
    if (!over || !canEdit) return;
    const activeCol = findCol(active.id);
    let overCol = findCol(over.id);
    if (!activeCol || !overCol) return;

    const items = byStatus[activeCol];
    const fromIdx = items.findIndex((a) => a.id === active.id);
    const a = items[fromIdx];

    if (activeCol === overCol) {
      const toIdx = items.findIndex((x) => x.id === over.id);
      if (toIdx === -1 || fromIdx === toIdx) return;
      const reordered = arrayMove(items, fromIdx, toIdx);
      const prev = reordered[toIdx - 1];
      const next = reordered[toIdx + 1];
      const newIndex = (prev ? prev.sort_index : (next ? next.sort_index - 2048 : 0))
        + (prev && next ? (next.sort_index - prev.sort_index) / 2 : 1024);
      setActions((p) => p.map((x) => x.id === a.id ? { ...x, sort_index: newIndex } : x));
      persistMove(a, activeCol, newIndex);
    } else {
      const target = byStatus[overCol].filter((x) => x.id !== a.id);
      const overIdx = target.findIndex((x) => x.id === over.id);
      const prev = overIdx > 0 ? target[overIdx - 1] : target[target.length - 1];
      const next = overIdx >= 0 ? target[overIdx] : null;
      let newIndex;
      if (prev && next) newIndex = (prev.sort_index + next.sort_index) / 2;
      else if (prev) newIndex = prev.sort_index + 1024;
      else if (next) newIndex = next.sort_index - 1024;
      else newIndex = 1024;
      applyMove(a.id, overCol, newIndex);
    }
  };

  const openCard = (a) => {
    if (canEdit) { setEditing(a); setFormOpen(true); }
    else setReadonlyCard(a);
  };

  const markDone = async (a, checked) => {
    if (!canEdit) return;
    snapshot.current = actions;
    setActions((prev) => prev.map((x) => x.id === a.id
      ? { ...x, status: checked ? "done" : "open", done_at: checked ? new Date().toISOString() : null } : x));
    try {
      await cmdApi.patch(`/actions/${a.id}`, { status: checked ? "done" : "open" });
    } catch (ex) {
      setActions(snapshot.current);
      toast.error(ex?.response?.data?.detail || "Could not update.");
    }
  };

  const buckets = useMemo(() => {
    const list = actions || [];
    const today = todayIST(), weekEnd = weekEndIST();
    const notDone = list.filter((a) => a.status !== "done");
    const done = list.filter((a) => a.status === "done")
      .sort((x, y) => String(y.done_at || "").localeCompare(String(x.done_at || "")));
    const b = {
      "Late": notDone.filter((a) => a.late),
      "Blocked": notDone.filter((a) => !a.late && a.status === "blocked"),
      "Due this week": notDone.filter((a) => !a.late && a.status !== "blocked" && a.due_date && a.due_date <= weekEnd),
      "Later": notDone.filter((a) => !a.late && a.status !== "blocked" && a.due_date && a.due_date > weekEnd),
      "No due date": notDone.filter((a) => !a.late && a.status !== "blocked" && !a.due_date),
      "Recently done": listDoneOnly ? done : done.slice(0, DONE_LIST_LIMIT),
    };
    return listDoneOnly ? { "Done": done } : b;
  }, [actions, listDoneOnly]);

  const filteredInitiatives = fVertical ? initiatives.filter((i) => i.vertical === fVertical) : initiatives;

  return (
    <section data-testid="cmd-board">
      <div className="cmd-toolbar" style={{ alignItems: "center" }}>
        <div className="cmd-view-toggle" role="tablist">
          <button className={`cmd-view-btn ${view === "kanban" ? "active" : ""}`} onClick={() => { setView("kanban"); setListDoneOnly(false); }}
            data-testid="view-kanban"><LayoutGrid size={14} /> Board</button>
          <button className={`cmd-view-btn ${view === "list" ? "active" : ""}`} onClick={() => setView("list")}
            data-testid="view-list"><List size={14} /> List</button>
        </div>
        <select className="cmd-input cmd-filter" value={fVertical}
          onChange={(e) => { setFVertical(e.target.value); setFInitiative(""); }} data-testid="filter-vertical">
          <option value="">All verticals</option>
          {["Coffee", "Dairy", "Hospitality", "Retail food", "Sri Lanka", "Technology"].map((v) => <option key={v}>{v}</option>)}
        </select>
        <select className="cmd-input cmd-filter" value={fInitiative} onChange={(e) => setFInitiative(e.target.value)}
          data-testid="filter-initiative">
          <option value="">All initiatives</option>
          {filteredInitiatives.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select className="cmd-input cmd-filter" value={fOwner} onChange={(e) => setFOwner(e.target.value)}
          data-testid="filter-owner">
          <option value="">Whole company</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {canEdit && (
          <button className="cmd-btn cmd-btn-primary" style={{ marginLeft: "auto" }}
            onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-action">
            <Plus size={16} style={{ marginRight: 6 }} />Add action
          </button>
        )}
      </div>

      {actions === null ? (
        <p className="cmd-muted">Loading…</p>
      ) : view === "kanban" ? (
        <DndContext sensors={sensors} collisionDetection={closestCorners}
          onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="cmd-board" data-testid="kanban">
            {COLUMNS.map((col) => (
              <Column key={col.key} col={col} cards={byStatus[col.key] || []} users={users}
                canEdit={canEdit} onOpen={openCard} onMoveStep={moveStep}
                onShowAllDone={() => { setView("list"); setListDoneOnly(true); }} />
            ))}
          </div>
        </DndContext>
      ) : (
        <div data-testid="list-view">
          {Object.entries(buckets).map(([name, rows]) => (
            <div key={name} style={{ marginBottom: 22 }}>
              <h3 className="cmd-vertical-head">{name} ({rows.length})</h3>
              {rows.length === 0
                ? <p className="cmd-muted" style={{ fontSize: 13, margin: "4px 0" }}>Nothing here.</p>
                : rows.map((a) => (
                    <div key={a.id} className={`cmd-list-row health-${a.initiative_health || "none"}`}
                      data-testid={`list-row-${a.id}`}>
                      {canEdit && (
                        <input type="checkbox" checked={a.status === "done"}
                          onChange={(e) => markDone(a, e.target.checked)}
                          aria-label={`Mark ${a.title} done`} data-testid={`tick-${a.id}`} />
                      )}
                      <div className="cmd-list-main" onClick={() => openCard(a)}>
                        <div className="cmd-card-title">{a.title}</div>
                        <div className="cmd-card-meta">
                          <span className="cmd-card-init">{a.initiative_name}</span>
                          {a.owner_role && <span className="cmd-chip">{a.owner_role}</span>}
                        </div>
                      </div>
                      <div className="cmd-card-foot" style={{ marginTop: 0 }}>
                        <DueLabel a={a} />
                        {a.priority === "high" && <span className="cmd-tag-high">High</span>}
                      </div>
                    </div>
                  ))}
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <Sheet onClose={() => { setFormOpen(false); setEditing(null); }}
          title={editing ? "Edit action" : "Add action"} testId="action-form-sheet">
          <ActionForm initial={editing} initiatives={initiatives}
            roles={roles.length ? roles : ["Founder", "Co-founder", "CFO", "Finance Director", "Vertical lead", "Staff"]}
            users={users}
            onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
            onClose={() => { setFormOpen(false); setEditing(null); }} />
        </Sheet>
      )}
      {readonlyCard && <ReadOnlyCard a={readonlyCard} users={users} onClose={() => setReadonlyCard(null)} />}
    </section>
  );
}
